import { heroTakeaway, keyTakeawaySchema } from "@/data/keyTakeaways";
import { useState } from "react";
import { lookupSlug } from "@/lib/slugLookup";
import { Navigate } from "@/lib/router-compat";
import { useStableLocation } from "@/hooks/useStableLocation";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import { DIVISION_LOGOS } from "@/lib/divisionLogos";
import CommercialBrowseSidebar from "@/components/services/CommercialBrowseSidebar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import WebinarRegistrationForm from "@/components/webinar/WebinarRegistrationForm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { getServiceBySlug } from "@/data/servicesData";
import ServiceHero from "@/components/services/ServiceHero";
import ServiceWhyQbd from "@/components/services/ServiceWhyQbd";
import ServiceRelatedContent from "@/components/services/ServiceRelatedContent";
import GenericOfferingContent from "@/components/services/GenericOfferingContent";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";

import { clinicalOfferingData } from "@/data/clinicalOfferingData";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import { getOfferingMeta, getLocalizedOfferingData, getOfferingUI } from "@/i18n/clinicalOfferings";
import { getServiceContent } from "@/i18n/serviceDetail";
import { localizeHref } from "@/lib/localizeHref";

interface FaqItem { q: string; a: string; }

import { SITE_URL as SITE, DEFAULT_OG_IMAGE } from "@/config/site";

const offeringPages: Record<string, {
  title: string;
  subtitle: string;
  description: string;
  contentKey: string;
  ogImage?: string;
  contentTags?: string[];
  faqItems?: FaqItem[];
  metaTitle?: string;
  ctaHeading?: string;
  ctaDescription?: string;
  metaDescription?: string;
  ctaLabel?: string;
}> = {
  "clinical-study-operations": {
    title: "Clinical Trial Operations",
    subtitle: "End-to-end support for your clinical trials",
    description: "Clinical study operations encompass the planning, execution, and management of clinical studies. At QbD Group, we focus on setting up and managing your clinical landscape to meet your specific objectives.",
    contentKey: "clinical-study-operations",
    contentTags: ["Clinical Solutions", "Medical Devices"],
  },
  "medical-writing-for-medical-devices": {
    title: "Medical Writing for Medical Devices",
    subtitle: "Ensuring regulatory compliance and patient safety",
    description: "High-quality medical writing is crucial for regulatory compliance and patient safety. Our team offers complete medical writing services that bridge the gap between scientific data and essential communication.",
    contentKey: "medical-writing-for-medical-devices",
    contentTags: ["Clinical Solutions", "Medical Devices"],
    faqItems: [
      { q: "My device is a low-risk class medical device, do I require a clinical evaluation?", a: "Yes, every medical device that will go to the market under MDR requires a clinical evaluation, starting with a state-of-the-art literature review and summarizing all data to provide clinical evidence on safety and performance of the medical device." },
      { q: "What type of documents are essential for clinical trials?", a: "Depending on the product under evaluation, the MDR or the ICH-GCP guidelines outline the required documents. In general, this includes Clinical Study Protocols, Informed Consent Forms, Patient Information Leaflets, Clinical Study Reports, and Investigator Brochures." }
    ],
  },
  "clinical-regulatory-services": {
    title: "Clinical Regulatory Services",
    subtitle: "Streamlining clinical investigation approvals",
    description: "Handling the complexities of clinical investigation approvals can be daunting. We streamline the process, ensuring you get the green light to start your study efficiently and in full compliance.",
    contentKey: "clinical-regulatory-services",
    contentTags: ["Clinical Solutions", "Regulatory Affairs"],
    faqItems: [
      { q: "Which countries have fast approval processes?", a: "The approval timelines vary by country and depend on factors like regulatory pathway, required documentation, fees, and contract processes. We use our country-specific knowledge to advise on the best approach based on your priorities, such as fast review times or low regulatory burden." },
      { q: "What documents are needed for submission?", a: "The core requirements are listed in MDR Annex XV Chapter II, but country-specific documents may be needed. These can include cover letters, power of attorney, GSPR checklist, site suitability forms, and more. Our team ensures all required documents are included and tailored to the specific country and study type." }
    ],
  },
  "pmcf": {
    title: "Post-Market Clinical Follow-up (PMCF)",
    subtitle: "Ensure long-term safety and compliance",
    description: "We help you navigate the complexities of PMCF with tailored, efficient, and MDR-compliant strategies that turn regulatory obligations into clinical and commercial value.",
    contentKey: "pmcf",
    contentTags: ["Clinical Solutions", "Medical Devices"],
  },
  "medical-writing-for-in-vitro-diagnostics": {
    title: "Medical Writing for In Vitro Diagnostics",
    subtitle: "Clear, precise, and compliant clinical evidence documentation",
    description: "When it comes to IVDs, having compliant clinical evidence documentation is critical. Our QbD IVD | Qarad team offers writing, consulting, and documentation services for IVD and CDx companies.",
    contentKey: "medical-writing-for-in-vitro-diagnostics",
    contentTags: ["Clinical Solutions", "IVD"],
  },
  "clinical-strategy-support": {
    title: "Clinical Strategy Support",
    subtitle: "Defining a robust clinical roadmap",
    description: "We are here to simplify the process of defining clinical strategy for your medical device, providing the expertise and guidance you need to define a robust clinical roadmap tailored to your unique challenges.",
    contentKey: "clinical-strategy-support",
    contentTags: ["Clinical Solutions", "Medical Devices"],
  },
  "clinical-pharmacovigilance-service": {
    title: "Clinical Pharmacovigilance Service",
    subtitle: "Comprehensive safety oversight for your clinical trials",
    description: "Pharmacovigilance is the science of detecting, assessing, and preventing adverse effects. At QbD Group, we offer full pharmacovigilance services for the safety management of your clinical trials.",
    contentKey: "clinical-pharmacovigilance-service",
    contentTags: ["Clinical Solutions", "Vigilance"],
  },
  "data-management-and-analysis": {
    title: "Clinical Data Management and Analysis",
    subtitle: "Ensuring accurate and compliant clinical data",
    description: "Our comprehensive clinical data management services are designed to support the entire data lifecycle, from planning and CRF design to data handling, cleaning, and study close-out.",
    contentKey: "data-management-and-analysis",
    contentTags: ["Clinical Solutions", "Medical Devices"],
  },
  "clinical-safety-management": {
    title: "Clinical Safety Management",
    subtitle: "Protecting participants and meeting regulatory standards",
    description: "Clinical Safety Management encompasses the processes and practices that ensure the safety, integrity, and ethical conduct of clinical investigations. We support you every step of the way.",
    contentKey: "clinical-safety-management",
    contentTags: ["Clinical Solutions", "Vigilance"],
  },
  "clinical-evidence-support": {
    title: "Clinical Evidence Support",
    subtitle: "Demonstrating scientific validity and performance for IVDR",
    description: "We provide expert support to streamline your clinical evidence strategy and medical writing needs, ensuring that your device meets regulatory expectations and reaches the market efficiently.",
    contentKey: "clinical-evidence-support",
    contentTags: ["Clinical Solutions", "IVD"],
    faqItems: [
      { q: "Do we need to perform clinical performance studies?", a: "Clinical performance can be demonstrated using one or a combination of: clinical performance studies (the preferred source), scientific peer-reviewed literature, or published experience from routine diagnostic testing. QbD IVD | Qarad will assess your specific case to determine if your existing clinical performance data is sufficiently robust." },
      { q: "Our IVDD device was certified over 10 years ago. Does this make us automatically compliant for IVDR submission?", a: "Unfortunately, no. The requirements under the IVD Regulation (IVDR) are significantly more extensive and stricter than those of the IVDD. One common challenge is creating an IVDR-compliant intended purpose, supported by strong clinical evidence to substantiate the claims. QbD IVD | Qarad can guide you in planning the necessary steps to transition your products from IVDD to IVDR compliance." }
    ],
  },
  "clinical-quality-services": {
    title: "Clinical Quality Services",
    subtitle: "Tailored support for all aspects of QMS",
    description: "Struggling to establish or maintain your Clinical Quality Management System (QMS)? Our clinical team has auditors and QA experts who specialize in providing tailored support for all aspects of QMS.",
    contentKey: "clinical-quality-services",
    contentTags: ["Clinical Solutions", "Quality Assurance"],
  },
  "clinical-expert-services": {
    title: "Clinical Expert Services and Flexible Resourcing",
    subtitle: "Functional service provision and flexible resourcing for clinical operations",
    description: "Add clinical research professionals to your team, in the countries where you need them. You manage the study. We fill the gap.\n\nWe work with medical device, IVD, pharma, biotech and hospital sponsors.",
    contentKey: "clinical-expert-services",
    contentTags: ["Clinical Solutions", "Medical Devices"],
    metaTitle: "Clinical Expert Services & Flexible Resourcing for Clinical Trials | QbD Group",
    metaDescription: "Add CRAs, CTAs and clinical project managers to your clinical team across Europe. Flexible resourcing and FSP support without outsourcing your full study.",
    ctaLabel: "Request a profile",
    ctaHeading: "Tell us which role you need",
    ctaDescription: "Send us the role, the country and the start date. We come back with profiles.",
    faqItems: [
      { q: "What is FSP, and how does it differ from full outsourcing?", a: "Functional service provision means we cover a function, or part of one, such as monitoring in the countries where you have no local team. We manage delivery against an agreed scope while you keep responsibility for the study. Flexible resourcing means adding individual professionals to your own team. With full outsourcing, we run the entire trial. We deliver all three, and small assignments carry no minimum programme size." },
      { q: "Do you work in pharma and biotech as well as medical devices?", a: "Yes. Our clinical research professionals work across medical device, IVD, pharma and biotech trials, under ICH-GCP, ISO 14155, MDR, IVDR and EU-CTR." },
      { q: "Do your people work under our SOPs?", a: "Yes. They join your team, follow your processes and work in your systems." },
      { q: "What is the smallest assignment you take?", a: "One professional, part-time. There is no minimum programme size." },
      { q: "Which countries do you cover?", a: "Our own CRAs, CTAs and project managers are based in Belgium, France, the Netherlands, Spain, Germany and Poland, and they travel across Europe to monitor sites outside their home country. Where a study needs presence we do not cover directly, our network extends further. Ask us about any market you need." },
      { q: "What is the anticipated cost for a flexible resourcing assignment?", a: "We typically work with an hourly/daily fee which depends on the profile, salary and expertise of the final candidate. Invoices are processed on a monthly basis based on the actual hours spent. In case there is a need for a different approach (e.g. fixed fee), this can be discussed as well." },
      { q: "Is there a minimum duration for a contract?", a: "No, the contract can be tailored according to your needs. For long-term support, the contracts are typically renewed on an annual basis and in case of unforeseen circumstances, contracts can be prematurely discontinued with a 1 month notice period." },
      { q: "Do you also offer headhunting services for permanent employees?", a: "This is no longer an active focus of QbD Clinical." }
    ],

  },
  "pmpf": {
    title: "Post-Market Performance Follow-up (PMPF)",
    subtitle: "Proactive collection and evaluation of IVD data",
    description: "At QbD IVD | Qarad, we help you turn PMPF into a practical, structured process—supporting your regulatory compliance and safeguarding patient outcomes.",
    contentKey: "pmpf",
    contentTags: ["Clinical Solutions", "IVD"],
  }
};

/** Map Spanish slugs → English content keys */
const ES_SLUG_MAP: Record<string, string> = {
  "operaciones-de-estudios-clínicos": "clinical-study-operations",
  "redacción-médica-para-productos-sanitarios": "medical-writing-for-medical-devices",
  "servicios-de-regulación-clínica": "clinical-regulatory-services",
  "seguimiento-clínico-postcomercialización": "pmcf",
  "redacción-médica-para-diagnósticos-in-vitro": "medical-writing-for-in-vitro-diagnostics",
  "soporte-estrategia-clinica": "clinical-strategy-support",
  "servicio-de-farmacovigilancia-clinica": "clinical-pharmacovigilance-service",
  "gestión-y-análisis-de-datos": "data-management-and-analysis",
  "gestión-de-la-seguridad-clínica": "clinical-safety-management",
  "soporte-en-evidencia-clínica": "clinical-evidence-support",
  "servicios-de-calidad-clínica": "clinical-quality-services",
  "servicios-de-clínicos-expertos": "clinical-expert-services",
  "servicios-de-clinicos-expertos": "clinical-expert-services",
  "servicios-de-expertos-clinicos": "clinical-expert-services",
};

/** Extract the offering slug from any pathname variant and resolve to content key */
const getOfferingSlug = (pathname: string): string | null => {
  const match = pathname.match(/\/(?:services|servicios)\/clinical-solutions\/([^/]+)$/);
  if (!match) return null;
  const raw = decodeURIComponent(match[1]);
  // If it's already an English key, return as-is; otherwise map from Spanish
  return lookupSlug(ES_SLUG_MAP, raw);
};

const ClinicalOffering = () => {
  const location = useStableLocation();
  const [formOpen, setFormOpen] = useState(false);
  const lang = useLanguage();
  const prefix = langPrefix(lang);
  const ui = getOfferingUI(lang);
  const servicesPath = localizeHref("/en/services", lang);
  const clinicalSolutionsPath = localizeHref("/en/services/clinical-solutions", lang);

  const slug = getOfferingSlug(location.pathname);
  const page = slug ? offeringPages[slug] : undefined;
  const parentService = getServiceBySlug("clinical-solutions");

  if (!page || !parentService) return <Navigate to={clinicalSolutionsPath} replace />;

  // Get translated parent service content
  const parentI18n = getServiceContent("clinical-solutions", lang);
  const displayParent = parentI18n
    ? { ...parentService, formHeading: parentI18n.formHeading, formSubtitle: parentI18n.formSubtitle }
    : parentService;

  // Get translated offering metadata
  const i18nMeta = getOfferingMeta(page.contentKey, lang);
  const displayTitle = i18nMeta?.title ?? page.title;
  const displaySubtitle = i18nMeta?.subtitle ?? page.subtitle;
  const displayDescription = i18nMeta?.description ?? page.description;
  const displayFaqItems = i18nMeta?.faqItems ?? page.faqItems;

  const heroService = {
    ...parentService,
    title: displayTitle,
    subtitle: displaySubtitle,
    description: displayDescription,
    path: location.pathname,
  };

  
  const pageOgImage = page.ogImage || DEFAULT_OG_IMAGE;
  const metaTitle = lang === "en" && page.metaTitle ? page.metaTitle : `${displayTitle} — ${ui.clinicalSolutions} | QbD Group`;
  const metaDesc = lang === "en" && page.metaDescription ? page.metaDescription : displayDescription.slice(0, 155);
  const pageUrl = `${SITE}${location.pathname}`;
  const relatedTags = page.contentTags || ["Clinical Solutions", "Medical Devices"];

  // Get localized offering content
  const rawData = clinicalOfferingData[page.contentKey];
  const data = rawData ? getLocalizedOfferingData(page.contentKey, rawData, lang) : undefined;

  return (
    <>
      <SEOHead title={metaTitle} description={metaDesc} canonical={pageUrl} ogImage={pageOgImage}>
        <script type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", "@type": "Service", name: displayTitle, description: displayDescription, provider: { "@type": "Organization", name: "QbD Group", url: SITE }, url: pageUrl, isPartOf: { "@type": "Service", name: parentService.title, url: `${SITE}${parentService.path}` }, ...keyTakeawaySchema(heroTakeaway(displaySubtitle, displayDescription)) })}
        </script>
        {displayFaqItems && displayFaqItems.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: displayFaqItems.map((faq) => ({
                "@type": "Question",
                name: faq.q,
                acceptedAnswer: { "@type": "Answer", text: faq.a },
              })),
            })}
          </script>
        )}
      </SEOHead>


      
      <BreadcrumbJsonLd
        items={[
          { name: lang === "es" ? "Servicios" : "Services", path: servicesPath },
          { name: ui.clinicalSolutions, path: clinicalSolutionsPath },
          { name: displayTitle },
        ]}
      />

      <ScrollToTop />
      <Navbar logoOverride={DIVISION_LOGOS["clinical-solutions"]} />
      <CommercialBrowseSidebar />

      <main className="pb-16 sm:pb-0" style={{ '--service-color': parentService.color } as React.CSSProperties}>
        <ServiceHero
          service={heroService}
          onCtaClick={() => setFormOpen(true)}
          hideDiscoverButton
          primaryCtaLabel={lang === "en" ? page.ctaLabel : undefined}
          parentCrumbs={[
            { label: ui.clinicalSolutions, to: clinicalSolutionsPath },
          ]}
        />

        {data && <GenericOfferingContent data={data} onCtaClick={() => setFormOpen(true)} hidePartnerSection />}

        <ServiceWhyQbd
          service={parentService}
          onCtaClick={() => setFormOpen(true)}
          partnerOverride={data ? { intro: data.partnerIntro, bullets: data.partnerBullets } : undefined}
        />

        {displayFaqItems && displayFaqItems.length > 0 && (
          <section className="py-16 sm:py-20 bg-muted/30">
            <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-6 h-0.5 rounded-full" style={{ backgroundColor: parentService.color }} />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{({ en: "Study planning and execution", es: "Planificaci\u00f3n y ejecuci\u00f3n", "zh-cn": "研究规划试验执行" }[lang])}</p>
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-6">{ui.faqTitle}</h2>
              <Accordion type="single" collapsible className="space-y-2">
                {displayFaqItems.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="bg-background border border-border/40 rounded-lg px-5">
                    <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        <ServiceRelatedContent service={parentService} contentTags={relatedTags} />

        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">{displayParent.formHeading}</DialogTitle>
            <WebinarRegistrationForm
              registrationUrl={null}
              title={parentService.title}
              hubspotFormId={parentService.hubspotFormId}
              heading={displayParent.formHeading}
              subtitle={displayParent.formSubtitle}
            />
          </DialogContent>
        </Dialog>

        <ContactCTA
          onContactClick={() => setFormOpen(true)}
          title={(lang === "en" && page.ctaHeading) || displayParent.formHeading}
          description={(lang === "en" && page.ctaDescription) || displayParent.formSubtitle}
          ctaLabel={lang === "en" ? page.ctaLabel : undefined}
        />
      </main>

      <Footer />
    </>
  );
};

export default ClinicalOffering;
